import { useEffect, useState } from "react";
import {
  TerminalSettingsRepository,
  type CustomFeeSettings,
  type FeeMode,
} from "../../application/settings/terminal_settings_repository";
import {
  PricingProfileRepository,
  type FreepayRates,
} from "../../application/settings/pricing_profile_repository";

const repo = new TerminalSettingsRepository();
const pricingRepo = new PricingProfileRepository();

const FEE_MODE_LABELS: Record<FeeMode, string> = {
  freepay: "Freepay",
  customFee: "Custom Fee",
  none: "No fee",
};

/** The banner describes the save that is pending, so it has to follow the
 * selected mode, not the stored one. */
function bothOnWarning(mode: FeeMode): string {
  if (mode === "none") return "Both fees are on; saving turns both off";
  // eslint-disable-next-line security/detect-object-injection -- mode is a FeeMode literal, not user input
  return `Both fees are on; saving keeps ${FEE_MODE_LABELS[mode]} only`;
}

function toNum(s: string): number {
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

function toInt(s: string): number {
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? 0 : n;
}

export function FeesTab({ businessId }: { businessId: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FeeMode>("none");
  const [bothOn, setBothOn] = useState(false);
  const [freepayDebit, setFreepayDebit] = useState(false);
  const [customFee, setCustomFee] = useState<CustomFeeSettings | null>(null);
  const [nameStr, setNameStr] = useState("");
  const [creditFeePercentStr, setCreditFeePercentStr] = useState("0");
  const [creditFeeDollars, setCreditFeeDollars] = useState("0.00");
  const [debitFeePercentStr, setDebitFeePercentStr] = useState("0");
  const [debitFeeDollars, setDebitFeeDollars] = useState("0.00");
  const [hasRates, setHasRates] = useState(false);
  const [rateSource, setRateSource] = useState<string | null>(null);
  const [creditPercentStr, setCreditPercentStr] = useState("0");
  const [creditFixedStr, setCreditFixedStr] = useState("0");
  const [debitPercentStr, setDebitPercentStr] = useState("0");
  const [debitFixedStr, setDebitFixedStr] = useState("0");
  // Cash rates are carried unedited so all six PricingProfile fields persist.
  const [cashRates, setCashRates] = useState({
    cashPercent: 0,
    cashFixedCents: 0,
  });

  useEffect(() => {
    let active = true;
    setCustomFee(null);
    setMode("none");
    setBothOn(false);
    setFreepayDebit(false);
    setNameStr("");
    setCreditFeePercentStr("0");
    setCreditFeeDollars("0.00");
    setDebitFeePercentStr("0");
    setDebitFeeDollars("0.00");
    setHasRates(false);
    setRateSource(null);
    setCreditPercentStr("0");
    setCreditFixedStr("0");
    setDebitPercentStr("0");
    setDebitFixedStr("0");
    setCashRates({ cashPercent: 0, cashFixedCents: 0 });
    setError(null);
    (async () => {
      setLoading(true);
      try {
        const [feeModeState, cf, r] = await Promise.all([
          repo.getFeeModeState(businessId),
          repo.getCustomFee(businessId),
          pricingRepo.getEffectiveRates(businessId),
        ]);
        if (!active) return;
        setMode(feeModeState.mode);
        setBothOn(feeModeState.bothOn);
        setFreepayDebit(feeModeState.freepayDebit);
        setCustomFee(cf);
        setNameStr(cf.name);
        setCreditFeePercentStr(String(cf.credit.percent));
        setCreditFeeDollars((cf.credit.flatCents / 100).toFixed(2));
        setDebitFeePercentStr(String(cf.debit.percent));
        setDebitFeeDollars((cf.debit.flatCents / 100).toFixed(2));
        setRateSource(r.source);
        setCreditPercentStr(String(r.creditPercent));
        setCreditFixedStr(String(r.creditFixedCents));
        setDebitPercentStr(String(r.debitPercent));
        setDebitFixedStr(String(r.debitFixedCents));
        setCashRates({
          cashPercent: r.cashPercent,
          cashFixedCents: r.cashFixedCents,
        });
        setHasRates(true);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [businessId]);

  async function save() {
    if (!customFee) return;
    setSaving(true);
    setError(null);

    const feeToSave: CustomFeeSettings = {
      name: nameStr,
      creditEnabled: customFee.creditEnabled,
      credit: {
        percent: toNum(creditFeePercentStr),
        flatCents: Math.round(toNum(creditFeeDollars) * 100),
      },
      debitEnabled: customFee.debitEnabled,
      debit: {
        percent: toNum(debitFeePercentStr),
        flatCents: Math.round(toNum(debitFeeDollars) * 100),
      },
    };

    // The fee's own fields are written in every mode: the form accepts them in
    // every mode, so gating the write on the active mode drops them silently —
    // the merchant configures the fee and switches to Freepay in one save.
    // Only saveFeeMode writes the mode flags, so this cannot change the active
    // fee, and it goes first so its validation rejects before the mode moves.
    const steps: Array<[string, () => Promise<void>]> = [
      ["custom fee fields", () => repo.saveCustomFee(businessId, feeToSave)],
      [
        "fee mode",
        async () => {
          await repo.saveFeeMode(businessId, mode, freepayDebit);
          // The mode write is what resolves a legacy both-on pair.
          setBothOn(false);
        },
      ],
      [
        "Freepay rates",
        () =>
          hasRates
            ? pricingRepo.saveBusinessRates(businessId, {
                creditPercent: toNum(creditPercentStr),
                creditFixedCents: toInt(creditFixedStr),
                debitPercent: toNum(debitPercentStr),
                debitFixedCents: toInt(debitFixedStr),
                ...cashRates,
              } satisfies FreepayRates)
            : Promise.resolve(),
      ],
    ];

    for (const [label, run] of steps) {
      try {
        await run();
      } catch (e) {
        setError(
          `Failed while saving ${label}: ` +
            (e instanceof Error ? e.message : String(e)),
        );
        setSaving(false);
        return;
      }
    }
    setSaving(false);
  }

  if (loading)
    return <div className="text-muted-foreground p-4 text-sm">Loading…</div>;
  if (!customFee)
    return <div className="p-4 text-sm text-red-600">{error}</div>;

  return (
    <div className="space-y-8 p-4">
      <section className="space-y-3">
        <h3 className="text-lg font-semibold">Fee type</h3>
        {bothOn && (
          <p className="text-sm text-amber-700">{bothOnWarning(mode)}</p>
        )}
        <div className="flex gap-6">
          {(["freepay", "customFee", "none"] as const).map((m) => (
            <label key={m} className="flex items-center gap-2">
              <input
                type="radio"
                name="fee-mode"
                value={m}
                checked={mode === m}
                // save() submits the mode captured when it started, so the
                // radios must not move while it runs.
                disabled={saving}
                onChange={() => setMode(m)}
              />
              {/* eslint-disable-next-line security/detect-object-injection -- m is a FeeMode literal, not user input */}
              <span>{FEE_MODE_LABELS[m]}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold">Custom Fee</h3>
        <p className="text-muted-foreground text-xs">
          The percent applies to the post-tax total. Cash is never charged a
          fee.
        </p>
        <label className="block">
          <span className="text-muted-foreground block text-sm">
            Name (on receipt)
          </span>
          <input
            className="mt-1 w-64 rounded border px-2 py-1"
            value={nameStr}
            onChange={(e) => setNameStr(e.target.value)}
          />
        </label>

        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={customFee.creditEnabled}
              onChange={(e) =>
                setCustomFee((prev) =>
                  prev
                    ? {
                        ...prev,
                        creditEnabled: e.target.checked,
                        debitEnabled: e.target.checked && prev.debitEnabled,
                      }
                    : prev,
                )
              }
            />
            <span>Credit enabled</span>
          </label>
          <div className="flex gap-3">
            <label className="block">
              <span className="text-muted-foreground block text-sm">
                Credit %
              </span>
              <input
                className="mt-1 w-28 rounded border px-2 py-1"
                inputMode="decimal"
                disabled={!customFee.creditEnabled}
                value={creditFeePercentStr}
                onChange={(e) => setCreditFeePercentStr(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-muted-foreground block text-sm">
                Credit flat ($)
              </span>
              <input
                className="mt-1 w-28 rounded border px-2 py-1"
                inputMode="decimal"
                disabled={!customFee.creditEnabled}
                value={creditFeeDollars}
                onChange={(e) => setCreditFeeDollars(e.target.value)}
                onBlur={() =>
                  setCreditFeeDollars(toNum(creditFeeDollars).toFixed(2))
                }
              />
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={customFee.debitEnabled}
              disabled={!customFee.creditEnabled}
              onChange={(e) =>
                setCustomFee((prev) =>
                  prev ? { ...prev, debitEnabled: e.target.checked } : prev,
                )
              }
            />
            <span>Debit enabled</span>
          </label>
          <div className="flex gap-3">
            <label className="block">
              <span className="text-muted-foreground block text-sm">
                Debit %
              </span>
              <input
                className="mt-1 w-28 rounded border px-2 py-1"
                inputMode="decimal"
                disabled={!customFee.creditEnabled}
                value={debitFeePercentStr}
                onChange={(e) => setDebitFeePercentStr(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-muted-foreground block text-sm">
                Debit flat ($)
              </span>
              <input
                className="mt-1 w-28 rounded border px-2 py-1"
                inputMode="decimal"
                disabled={!customFee.creditEnabled}
                value={debitFeeDollars}
                onChange={(e) => setDebitFeeDollars(e.target.value)}
                onBlur={() =>
                  setDebitFeeDollars(toNum(debitFeeDollars).toFixed(2))
                }
              />
            </label>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold">Freepay</h3>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={freepayDebit}
            disabled={mode !== "freepay" || saving}
            onChange={(e) => setFreepayDebit(e.target.checked)}
          />
          <span>Freepay debit enabled</span>
        </label>

        {hasRates && (
          <div className="space-y-3">
            <p className="text-muted-foreground text-xs">
              Current source: {rateSource ?? "unknown"} (editing here creates a
              per-business override)
            </p>
            <div className="flex gap-3">
              <label className="block">
                <span className="text-muted-foreground block text-sm">
                  Freepay credit rate %
                </span>
                <input
                  className="mt-1 w-28 rounded border px-2 py-1"
                  inputMode="decimal"
                  value={creditPercentStr}
                  onChange={(e) => setCreditPercentStr(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-muted-foreground block text-sm">
                  Credit fixed (¢)
                </span>
                <input
                  className="mt-1 w-28 rounded border px-2 py-1"
                  inputMode="numeric"
                  value={creditFixedStr}
                  onChange={(e) => setCreditFixedStr(e.target.value)}
                />
              </label>
            </div>
            <div className="flex gap-3">
              <label className="block">
                <span className="text-muted-foreground block text-sm">
                  Freepay debit rate %
                </span>
                <input
                  className="mt-1 w-28 rounded border px-2 py-1"
                  inputMode="decimal"
                  value={debitPercentStr}
                  onChange={(e) => setDebitPercentStr(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-muted-foreground block text-sm">
                  Debit fixed (¢)
                </span>
                <input
                  className="mt-1 w-28 rounded border px-2 py-1"
                  inputMode="numeric"
                  value={debitFixedStr}
                  onChange={(e) => setDebitFixedStr(e.target.value)}
                />
              </label>
            </div>
          </div>
        )}
      </section>

      {error && <div className="text-sm text-red-600">{error}</div>}
      <button
        className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
        disabled={saving}
        onClick={save}
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
